/**
 * Кабинет заказов склада: очередь, сбор, собранные, на отправку.
 * Видно только кладовщикам/складу и админу.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Modal from 'react-modal';
import { FiPackage, FiUser, FiPrinter, FiEdit2, FiPlus, FiX, FiCamera, FiCheckCircle, FiXCircle, FiSearch, FiChevronLeft, FiChevronRight, FiBarChart2, FiLink, FiCopy, FiTrash2, FiUpload, FiUsers, FiShuffle, FiRefreshCw } from 'react-icons/fi';
import ModalCloseButton from '../Common/ModalCloseButton';
import { useApp } from '../../context/AppContext';
import './WarehouseOrdersModal.css';

// Опционально: пакет html5-qrcode (npm i html5-qrcode). Если не установлен — сканер штрихкодов недоступен.
let Html5Qrcode = null;
try {
  Html5Qrcode = require('html5-qrcode').Html5Qrcode;
} catch (_) {}

Modal.setAppElement('#root');

const TABS = [
  { key: 'queue', label: 'На очереди', status: 'queue' },
  { key: 'in_progress', label: 'Собираются', status: 'in_progress' },
  { key: 'collected', label: 'Собрано', status: 'collected' },
  { key: 'shipping', label: 'На отправку', status: 'shipping' }
];

function formatOrdersCountRu(n) {
  const abs = Math.abs(Number(n) || 0);
  const mod100 = abs % 100;
  const mod10 = abs % 10;
  if (mod100 >= 11 && mod100 <= 19) return `${n} заказов`;
  if (mod10 === 1) return `${n} заказ`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} заказа`;
  return `${n} заказов`;
}

const ORDER_STATUS_LABEL = {
  queue: 'В очереди',
  in_progress: 'Собирается',
  collected: 'Собрано',
  shipping: 'На отправке'
};

function orderStatusLabel(status) {
  return ORDER_STATUS_LABEL[status] || status || '—';
}

/** Число позиций в заказе: из списка API (items_count) или длина вложенного массива items. */
function warehouseInvoiceItemCombinedText(item) {
  const joined = [item.title, item.article].filter(Boolean).map((s) => String(s).trim()).join(' ');
  return joined.replace(/\s+/g, ' ').trim() || String(item.article || item.title || '').trim();
}

function looksLikeWarehouseSkuLine(t) {
  if (!t) return false;
  return (
    /[A-Za-zА-Яа-яЁё]{1,8}\d{2,}\s*[-_]/i.test(t) ||
    /[_][А-Яа-яA-Za-zЁё]/.test(t) ||
    /\d{3,}\s*[-–]\s*\d{3,}/.test(t)
  );
}

/** Строка накладной (кладовщик/менеджер/дата), не товар — скрываем из таблицы сбора. */
function warehouseOrderItemIsInvoiceMetadata(item) {
  const t = warehouseInvoiceItemCombinedText(item);
  if (!t) return false;
  if (/ответственный\s+кладовщик/i.test(t)) return true;
  if (/^\s*ответственный\b/i.test(t) && t.length < 120) return true;
  if (/\(кладовщик\)/i.test(t) && !looksLikeWarehouseSkuLine(t)) return true;
  if (/\d{2}\.\d{2}\.\d{4}\s+\d{1,2}:\d{2}/.test(t) && /менеджер/i.test(t)) return true;
  if (/менеджер\s+кц/i.test(t) && !looksLikeWarehouseSkuLine(t) && t.length < 180) return true;
  return false;
}

function splitWarehouseOrderItemsForPicking(items) {
  const raw = Array.isArray(items) ? items : [];
  const pickableItems = [];
  const metadataLines = [];
  for (const it of raw) {
    if (warehouseOrderItemIsInvoiceMetadata(it)) {
      const line = warehouseInvoiceItemCombinedText(it);
      if (line) metadataLines.push(line);
    } else {
      pickableItems.push(it);
    }
  }
  return { pickableItems, metadataLines };
}

function orderLineItemsCount(order) {
  if (!order) return 0;
  if (Array.isArray(order.items)) {
    return splitWarehouseOrderItemsForPicking(order.items).pickableItems.length;
  }
  const fromApi = order.items_count ?? order.itemsCount;
  if (fromApi != null && fromApi !== '') {
    const n = Number(fromApi);
    if (Number.isFinite(n)) return Math.max(0, n);
  }
  return 0;
}

/** Пустая строка или нечисло → null; иначе целое ≥ 0 */
function parsePositionsBound(raw) {
  const t = (raw || '').trim();
  if (t === '') return null;
  const n = parseInt(t, 10);
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

export default function WarehouseOrdersModal({ isOpen, onRequestClose }) {
  const { state: appState } = useApp();
  const [activeTab, setActiveTab] = useState('queue');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pickingOrder, setPickingOrder] = useState(null);
  const [addOrderOpen, setAddOrderOpen] = useState(false);
  const [editOrderOpen, setEditOrderOpen] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [positionsMin, setPositionsMin] = useState('');
  const [positionsMax, setPositionsMax] = useState('');
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [importExcelOpen, setImportExcelOpen] = useState(false);
  const [showDistributionModal, setShowDistributionModal] = useState(false);
  /** Только сборщик: число заказов «Собираются», назначенных ему (обновляется при каждой загрузке списка). */
  const [myPickingCount, setMyPickingCount] = useState(null);

  const user = appState?.user || (typeof localStorage !== 'undefined' ? (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })() : {});
  const role = (user.role || '').toLowerCase();
  const position = (user.position || '').toLowerCase();
  const department = (user.department || '').toLowerCase();
  const isAdmin = role === 'admin';
  const isHr = role === 'hr';
  const isDirector = /директор|director/i.test(position) || /директор|director/i.test(department);
  const isSeniorWarehouse = /старший/i.test(user.position || '') && (/склад|кладовщик|сборщик/i.test(department) || /склад|кладовщик|сборщик/i.test(position));
  const canSeeStats = isAdmin || isHr || isDirector || isSeniorWarehouse;
  const isWarehouseStaff = isAdmin || /склад|кладовщик|сборщик|логистик/i.test(department) || /склад|кладовщик|сборщик/i.test(position);
  const isPickerOnly = isWarehouseStaff && !canSeeStats;
  const canImportExcel = canSeeStats;

  const statusForTab = TABS.find(t => t.key === activeTab)?.status || 'queue';

  const filteredOrders = useMemo(() => {
    let list = orders;
    let lo = parsePositionsBound(positionsMin);
    let hi = parsePositionsBound(positionsMax);
    if (lo != null && hi != null && lo > hi) [lo, hi] = [hi, lo];
    const hasPosFilter = lo != null || hi != null;
    if (hasPosFilter) {
      list = list.filter(o => {
        const c = orderLineItemsCount(o);
        if (lo != null && c < lo) return false;
        if (hi != null && c > hi) return false;
        return true;
      });
    }

    const q = (searchQuery || '').trim().toLowerCase();
    if (!q) return list;
    const idStr = (v) => (v != null ? String(v) : '');
    return list.filter(o => {
      const id = idStr(o.id);
      const ext = idStr(o.external_id);
      const name = (o.np_recipient_name || '').toLowerCase();
      const last4 = id.slice(-4);
      return id.includes(q) || ext.toLowerCase().includes(q) || name.includes(q) || (q.length === 4 && last4 === q) || (q.length <= 4 && last4.includes(q));
    });
  }, [orders, searchQuery, positionsMin, positionsMax]);

  const fetchOrders = useCallback(() => {
    setLoading(true);
    setError('');
    fetch(`/api/warehouse-orders?status=${statusForTab}`, { headers: getAuthHeaders() })
      .then(r => {
        if (r.ok) return r.json();
        if (r.status === 404) return Promise.reject(new Error('Сервис заказов не настроен на сервере (404). Подключите роут /api/warehouse-orders.'));
        return Promise.reject(new Error(r.status === 401 ? 'Нужна авторизация' : 'Ошибка загрузки'));
      })
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(e => {
        setError(e.message || 'Не удалось загрузить заказы');
        setOrders([]);
      })
      .finally(() => {
        setLoading(false);
        if (isPickerOnly) {
          fetch('/api/warehouse-orders?status=in_progress', { headers: getAuthHeaders() })
            .then(r => (r.ok ? r.json() : []))
            .then(data => setMyPickingCount(Array.isArray(data) ? data.length : 0))
            .catch(() => setMyPickingCount(0));
        } else {
          setMyPickingCount(null);
        }
      });
  }, [statusForTab, isPickerOnly]);

  useEffect(() => {
    if (!isOpen) {
      setMyPickingCount(null);
      return;
    }
    fetchOrders();
  }, [isOpen, fetchOrders]);

  useEffect(() => {
    if (!isOpen || !addOrderOpen) return;
    fetch('/api/users', { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, [isOpen, addOrderOpen]);

  const handleAssign = (orderId, userId) => {
    if (!userId) return;
    fetch(`/api/warehouse-orders/${orderId}/assign`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: Number(userId) })
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => fetchOrders())
      .catch(() => setError('Не удалось назначить'));
  };

  const handleStatusChange = (orderId, status) => {
    return fetch(`/api/warehouse-orders/${orderId}/status`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status })
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => { fetchOrders(); setPickingOrder(null); })
      .catch(() => { setError('Не удалось обновить статус'); return Promise.reject(); });
  };

  const handleMarkShipping = (orderId, waybillNumber) => {
    fetch(`/api/warehouse-orders/${orderId}/shipping`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ waybillNumber: waybillNumber || '' })
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => { fetchOrders(); setPickingOrder(null); })
      .catch(() => setError('Не удалось отметить отправку'));
  };

  const handlePrintWaybill = (order) => {
    // Если из 1С пришла ссылка на готовый PDF-ярлык НП — просто открываем его
    if (order.np_waybill_pdf_url) {
      window.open(order.np_waybill_pdf_url, '_blank', 'noopener,noreferrer');
      return;
    }
    // Fallback: если ссылки нет, можно напечатать простой текстовый ярлык
    const w = window.open('', '_blank', 'width=400,height=500');
    if (!w) return;
    w.document.write(`
      <!DOCTYPE html><html><head><title>Накладная НП ${order.id}</title>
      <style>
        body { font-family: Arial,sans-serif; padding: 16px; font-size: 14px; }
        .label { border: 1px dashed #333; padding: 12px; margin-bottom: 12px; max-width: 380px; }
        .label h3 { margin: 0 0 8px 0; font-size: 16px; }
        .label p { margin: 4px 0; }
        @media print { body { padding: 0; } .no-print { display: none; } }
      </style></head><body>
      <div class="label">
        <h3>Новая Почта — заказ #${order.id}</h3>
        <p><b>Отримувач:</b> ${(order.np_recipient_name || '').replace(/</g, '&lt;')}</p>
        <p><b>Телефон:</b> ${(order.np_recipient_phone || '').replace(/</g, '&lt;')}</p>
        <p><b>Місто:</b> ${(order.np_city || '').replace(/</g, '&lt;')}</p>
        <p><b>Відділення:</b> ${(order.np_warehouse_address || order.np_warehouse_id || '').replace(/</g, '&lt;')}</p>
        <p><b>Накладная:</b> ${(order.np_waybill_number || '').replace(/</g, '&lt;')}</p>
      </div>
      <p class="no-print"><button onclick="window.print()">Печать</button> <button onclick="window.close()">Закрыть</button></p>
      <script>window.onload = function() { setTimeout(function() { window.print(); }, 300); };</script>
      </body></html>
    `);
    w.document.close();
  };

  if (!isOpen) return null;

  return (
    <div
      className="warehouse-orders-overlay"
      onClick={onRequestClose}
      role="dialog"
      aria-modal="true"
      aria-label="Заказы склада"
    >
      <div className="warehouse-orders-modal" onClick={(e) => e.stopPropagation()}>
        <div className="warehouse-orders-content">
        <div className="warehouse-orders-header">
          <h2 className="warehouse-orders-title-row">
            <span className="warehouse-orders-title-main">
              <FiPackage size={24} /> Заказы
            </span>
            {isPickerOnly && myPickingCount != null && (
              <span
                className="warehouse-orders-my-picking-badge"
                title="Заказы в статусе «Собираются», назначенные вам"
              >
                На сборку: <strong>{myPickingCount}</strong>
              </span>
            )}
          </h2>
          <ModalCloseButton onClick={onRequestClose} />
        </div>

        <div className="warehouse-orders-tabs">
          {TABS.map(t => (
            <button
              key={t.key}
              type="button"
              className={activeTab === t.key ? 'active' : ''}
              onClick={() => setActiveTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="warehouse-orders-filters-bar">
          <div className="warehouse-orders-search">
            <FiSearch className="warehouse-orders-search-icon" />
            <input
              type="text"
              className="warehouse-orders-search-input"
              placeholder="Поиск: № заказа, последние 4 цифры, имя заказчика"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="warehouse-orders-search-clear" onClick={() => setSearchQuery('')} aria-label="Очистить поиск"><FiX /></button>
            )}
          </div>
          <div className="warehouse-orders-positions-filter" role="group" aria-label="Фильтр по числу позиций в заказе">
            <span className="warehouse-orders-positions-filter-label">Позиций в заказе:</span>
            <label className="warehouse-orders-positions-filter-field">
              <span className="warehouse-orders-positions-filter-sr">от</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                className="warehouse-orders-positions-input"
                placeholder="от"
                value={positionsMin}
                onChange={e => setPositionsMin(e.target.value)}
                aria-label="Минимум позиций"
              />
            </label>
            <span className="warehouse-orders-positions-filter-dash">—</span>
            <label className="warehouse-orders-positions-filter-field">
              <span className="warehouse-orders-positions-filter-sr">до</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                step={1}
                className="warehouse-orders-positions-input"
                placeholder="до"
                value={positionsMax}
                onChange={e => setPositionsMax(e.target.value)}
                aria-label="Максимум позиций"
              />
            </label>
            {(positionsMin.trim() || positionsMax.trim()) && (
              <button
                type="button"
                className="warehouse-orders-positions-filter-clear"
                onClick={() => { setPositionsMin(''); setPositionsMax(''); }}
                aria-label="Сбросить фильтр по позициям"
              >
                Сброс
              </button>
            )}
          </div>
        </div>

        {isPickerOnly && (
          <div className="warehouse-orders-picker-hint" role="status">
            {myPickingCount != null && (
              <div className="warehouse-orders-picker-count-line">
                Сейчас у вас <strong>{formatOrdersCountRu(myPickingCount)}</strong> на сборку (вкладка «Собираются»).
              </div>
            )}
            <div className="warehouse-orders-picker-hint-text">
              Показаны только заказы, назначенные вам. Новые задания появятся после распределения старшим кладовщиком или администратором.
            </div>
          </div>
        )}

        {error && <div className="warehouse-orders-error">{error}</div>}
        {loading && <div className="warehouse-orders-loading">Загрузка...</div>}

        {!loading && !error && (
          <div className="warehouse-orders-list">
            {filteredOrders.length === 0 ? (
              <p className="warehouse-orders-empty">{searchQuery ? 'Ничего не найдено' : 'Нет заказов'}</p>
            ) : (
              filteredOrders.map(o => (
                <OrderCard
                  key={o.id}
                  order={o}
                  status={statusForTab}
                  canAssign={canSeeStats}
                  onAssign={handleAssign}
                  onOpenPicking={() => setPickingOrder(o)}
                  onMarkCollected={() => handleStatusChange(o.id, 'collected')}
                  onMarkShipping={handleMarkShipping}
                  onPrintWaybill={handlePrintWaybill}
                  onEdit={() => setEditOrderOpen(o)}
                  fetchOrders={fetchOrders}
                />
              ))
            )}
          </div>
        )}

        {activeTab === 'queue' && (
          <div className="warehouse-orders-footer">
            <button type="button" className="btn-connection-1c" onClick={() => setShowConnectionModal(true)}>
              <FiLink /> Подключение 1С
            </button>
            {canSeeStats && (
              <button type="button" className="btn-stats-by-assignee" onClick={() => setShowStatsModal(true)}>
                <FiBarChart2 /> Мониторинг сборки
              </button>
            )}
            {canSeeStats && (
              <button type="button" className="btn-distribute-orders" onClick={() => setShowDistributionModal(true)}>
                <FiUsers /> Распределить заказы
              </button>
            )}
            {canImportExcel && (
              <button type="button" className="btn-import-excel" onClick={() => setImportExcelOpen(true)}>
                <FiUpload /> Импорт Excel
              </button>
            )}
            <button type="button" className="btn-add-order" onClick={() => setAddOrderOpen(true)}>
              <FiPlus /> Добавить заказ
            </button>
          </div>
        )}
      </div>

      {pickingOrder && (
        <PickingModal
          nested
          order={pickingOrder}
          ordersList={filteredOrders}
          onClose={() => setPickingOrder(null)}
          onSwitchOrder={setPickingOrder}
          onCollected={() => {
            handleStatusChange(pickingOrder.id, 'collected').then(() => {
              fetch(`/api/warehouse-orders?status=in_progress`, { headers: getAuthHeaders() })
                .then(r => r.ok ? r.json() : [])
                .then(list => { if (Array.isArray(list) && list.length) setPickingOrder(list[0]); })
                .catch(() => {});
            });
          }}
          onShipping={(waybill) => handleMarkShipping(pickingOrder.id, waybill)}
          onRefresh={fetchOrders}
        />
      )}

      {addOrderOpen && (
        <AddOrderModal
          onClose={() => setAddOrderOpen(false)}
          onSaved={() => { setAddOrderOpen(false); fetchOrders(); }}
        />
      )}

      {editOrderOpen && (
        <EditOrderModal
          order={editOrderOpen}
          onClose={() => setEditOrderOpen(null)}
          onSaved={() => { setEditOrderOpen(null); fetchOrders(); }}
        />
      )}

      {importExcelOpen && (
        <ImportOrdersExcelModal
          onClose={() => setImportExcelOpen(false)}
          onImported={() => { setImportExcelOpen(false); fetchOrders(); }}
        />
      )}

      {showStatsModal && (
        <WarehouseOrdersStatsModal onClose={() => setShowStatsModal(false)} />
      )}

      {showDistributionModal && (
        <BulkDistributionModal
          onClose={() => setShowDistributionModal(false)}
          onAssigned={(options) => {
            fetchOrders();
            if (!options?.keepOpen) setShowDistributionModal(false);
          }}
        />
      )}

      {showConnectionModal && (
        <WarehouseConnection1CModal
          onClose={() => setShowConnectionModal(false)}
          onTestSuccess={() => { setShowConnectionModal(false); fetchOrders(); }}
        />
      )}
      </div>
      </div>
  );
}

function OrderCard({ order, status, canAssign, onAssign, onOpenPicking, onMarkCollected, onMarkShipping, onPrintWaybill, onEdit, fetchOrders }) {
  const [assignUserId, setAssignUserId] = useState('');
  const [users, setUsers] = useState([]);
  const [waybill, setWaybill] = useState(order.np_waybill_number || '');

  useEffect(() => {
    fetch('/api/users', { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : [])
      .then(data => setUsers(Array.isArray(data) ? data : []))
      .catch(() => setUsers([]));
  }, []);

  const warehouseUsers = users.filter(u => {
    const d = (u.department || '').toLowerCase();
    const p = (u.position || '').toLowerCase();
    return /склад|кладовщик|сборщик|логистик/i.test(d) || /склад|кладовщик|сборщик/i.test(p) || u.role === 'admin';
  });

  const dbStatus = order.status || status;

  return (
    <div className="order-card">
      <div className="order-card-main">
        <span className="order-id">{order.external_id || `#${order.id}`}</span>
        {order.external_id && <span className="order-ext">{order.external_id}</span>}
        <span
          className="order-card-items-count"
          title="Количество позиций (наименований) в заказе без открытия сборки"
        >
          {orderLineItemsCount(order)} поз.
        </span>
        <span className={`order-status-badge order-status-badge--${dbStatus}`} title="Текущий статус заказа в системе">
          {orderStatusLabel(dbStatus)}
        </span>
        {order.assigned_username && <span className="order-assigned"><FiUser /> {order.assigned_username}</span>}
        {order.np_recipient_name && <span className="order-recipient">{order.np_recipient_name}</span>}
      </div>
      <div className="order-card-actions">
        {status === 'queue' && canAssign && (
          <div className="order-assign">
            <select value={assignUserId} onChange={e => setAssignUserId(e.target.value)}>
              <option value="">Назначить сборщика</option>
              {warehouseUsers.map(u => (
                <option key={u.id} value={u.id}>{u.username}</option>
              ))}
            </select>
            <button type="button" onClick={() => onAssign(order.id, assignUserId)}>Назначить</button>
          </div>
        )}
        {(status === 'queue' || status === 'in_progress') && (
          <button type="button" className="btn-pick" onClick={onOpenPicking}>Сбор товаров</button>
        )}
        {status === 'in_progress' && (
          <button type="button" className="btn-done" onClick={onMarkCollected} title="Перевести заказ в статус «Собрано» после фактической сборки">
            Отметить «Собрано»
          </button>
        )}
        {status === 'collected' && (
          <div className="order-shipping">
            <input
              type="text"
              placeholder="Номер накладной НП"
              value={waybill}
              onChange={e => setWaybill(e.target.value)}
            />
            <div className="order-shipping-actions">
              <button
                type="button"
                onClick={() => onPrintWaybill({ ...order, np_waybill_number: waybill })}
                title="Открыть готовый PDF-ярлык или простой макет для печати"
              >
                <FiPrinter /> Печать
              </button>
              <button type="button" onClick={() => onMarkShipping(order.id, waybill)}>На отправку</button>
            </div>
          </div>
        )}
        <button type="button" className="btn-edit" onClick={onEdit} title="Изменить"><FiEdit2 /></button>
      </div>
    </div>
  );
}

function PickingItemRow({ item, qtyPicked, maxQty, onQtyChange, onSaveItem, onRemove }) {
  const isFullyPicked = qtyPicked >= maxQty;
  const preventBlur = (e) => e.preventDefault();

  const title =
    (item.title || item.product_title || item.productTitle || '').trim() ||
    [item.article, item.color].filter(Boolean).join('_') +
      (item.size ? `, ${item.size}` : '');

  const inc = () => onQtyChange(item.id, (prev) => Math.min(maxQty, prev + 1));
  const dec = () => onQtyChange(item.id, (prev) => Math.max(0, prev - 1));

  const onInputChange = (e) => {
    const raw = e.target.value;
    if (raw === '' || raw === '-') {
      onQtyChange(item.id, 0);
      return;
    }
    const num = parseInt(raw, 10);
    if (!isNaN(num)) onQtyChange(item.id, Math.min(maxQty, Math.max(0, num)));
  };

  return (
    <tr className={`picking-table-row ${isFullyPicked ? 'done' : ''}`}>
      <td colSpan={2} className="picking-row-td-merged">
        <div className="picking-row-card">
          <div className="picking-td-title picking-row-card-title">
            <span className={`picking-check ${isFullyPicked ? 'ok' : ''}`}>
              <FiCheckCircle />
            </span>
            <span className="picking-title-text">{title}</span>
          </div>
          <div className="picking-td-qty picking-row-card-qty">
            <div className="picking-qty-required">
              Нужно: <b>{maxQty}</b> · Собрано: <b>{qtyPicked}</b>
            </div>
            <div className="picking-qty-controls" onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}>
              <button type="button" className="qty-pick-btn qty-pick-btn-minus" onMouseDown={preventBlur} onClick={dec} aria-label="Уменьшить">−</button>
              <input
                type="number"
                min={0}
                max={maxQty}
                value={qtyPicked}
                onChange={onInputChange}
                onBlur={(e) => {
                  const val = parseInt(e.target.value, 10);
                  onSaveItem(item.id, isNaN(val) ? 0 : Math.min(maxQty, Math.max(0, val)));
                }}
                className="qty-pick-input"
                aria-label={`Собрано ${title}`}
              />
              <button type="button" className="qty-pick-btn qty-pick-btn-plus" onMouseDown={preventBlur} onClick={inc} aria-label="Увеличить">+</button>
            </div>
            {!isFullyPicked && (
              <button
                type="button"
                className="btn-remove-from-order btn-remove-from-order-text"
                onClick={() => onRemove(item)}
                title="Нет на складе — удалить позицию из заказа"
              >
                Нет на складе
              </button>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function PickingModal({ order, ordersList = [], onClose, onSwitchOrder, onCollected, onShipping, onRefresh, nested = false }) {
  const [detail, setDetail] = useState(null);
  const [picked, setPicked] = useState({});
  const pickedRef = useRef({});
  const [, setRenderTick] = useState(0);
  const [loading, setLoading] = useState(true);
  const [orderMetaOpen, setOrderMetaOpen] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scanMode, setScanMode] = useState('add');
  const [scanMessage, setScanMessage] = useState(null);
  const [wrongScanPrompt, setWrongScanPrompt] = useState(null);
  const [removeItemPrompt, setRemoveItemPrompt] = useState(null);
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState(null);
  const lastScannedRef = useRef({ code: '', at: 0 });
  const touchStartRef = useRef({ x: 0 });
  const touchEndRef = useRef({ x: 0 });

  useEffect(() => {
    setOrderMetaOpen(false);
    fetch(`/api/warehouse-orders/${order.id}`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(data => {
        setDetail(data);
        const items = data.items || [];
        const initial = {};
        items.forEach(it => {
          const k = it.id != null ? String(it.id) : it.id;
          initial[k] = it.quantity_picked ?? it.quantityPicked ?? 0;
        });
        setPicked(initial);
        pickedRef.current = { ...initial };
      })
      .finally(() => setLoading(false));
  }, [order.id]);

  const { pickableItems, metadataLines } = useMemo(
    () => splitWarehouseOrderItemsForPicking(detail?.items),
    [detail?.items]
  );

  const mergedOrder = useMemo(() => ({ ...order, ...(detail || {}) }), [order, detail]);

  const pickingSummaryLine = useMemo(() => {
    const o = mergedOrder;
    const parts = [];
    if (o.np_recipient_name) parts.push(`Получатель: ${o.np_recipient_name}`);
    if (o.assigned_username) parts.push(`Сборщик: ${o.assigned_username}`);
    return parts.join(' · ') || null;
  }, [mergedOrder]);

  const list = Array.isArray(ordersList) ? ordersList : [];
  const currentIndex = list.findIndex(o => o.id === order.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < list.length - 1;
  const canSwipe = list.length > 1;

  const handleSwipePrev = () => { if (hasPrev && onSwitchOrder) onSwitchOrder(list[currentIndex - 1]); };
  const handleSwipeNext = () => { if (hasNext && onSwitchOrder) onSwitchOrder(list[currentIndex + 1]); };

  const handleTouchStart = (e) => {
    if (!canSwipe || !onSwitchOrder) return;
    touchStartRef.current.x = e.touches?.[0]?.clientX ?? 0;
  };
  const handleTouchEnd = (e) => {
    if (!canSwipe || !onSwitchOrder) return;
    touchEndRef.current.x = e.changedTouches?.[0]?.clientX ?? 0;
    const delta = touchEndRef.current.x - touchStartRef.current.x;
    const threshold = 50;
    if (delta > threshold) handleSwipePrev();
    else if (delta < -threshold) handleSwipeNext();
  };

  const savePickedForItem = useCallback((itemId, quantity) => {
    fetch(`/api/warehouse-orders/${order.id}/items/${itemId}/picked`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantityPicked: quantity })
    }).catch(() => {});
  }, [order.id]);

  const onQtyChange = useCallback((itemId, newValOrUpdater) => {
    const key = itemId != null ? String(itemId) : itemId;
    const prevObj = pickedRef.current;
    const current = prevObj[key] ?? 0;
    const nextVal = typeof newValOrUpdater === 'function'
      ? newValOrUpdater(current)
      : newValOrUpdater;
    const next = { ...prevObj, [key]: nextVal };
    pickedRef.current = next;
    setPicked(next);
    setRenderTick(t => t + 1);
    savePickedForItem(itemId, nextVal);
  }, [savePickedForItem]);

  const applyScannedBarcode = useCallback((barcodeRaw) => {
    const normalized = String(barcodeRaw || '').trim().replace(/\s/g, '');
    if (!normalized) return;
    const now = Date.now();
    if (lastScannedRef.current.code === normalized && now - lastScannedRef.current.at < 2500) return;
    lastScannedRef.current = { code: normalized, at: now };

    const items = pickableItems;
    const match = items.find(it => {
      const b = (it.barcode || '').trim().replace(/\s/g, '');
      const a = (it.article || '').trim().replace(/\s/g, '');
      return b === normalized || a === normalized || (b && normalized.endsWith(b)) || (a && normalized.includes(a));
    });
    if (!match) {
      setWrongScanPrompt({ barcode: normalized });
      if (navigator.vibrate) navigator.vibrate(200);
      return;
    }
    const idx = items.indexOf(match);
    if (idx < 0) return;
    const cur = pickedRef.current[String(match.id)] ?? picked[String(match.id)] ?? 0;
    const isRemove = scanMode === 'remove';
    const nextQty = isRemove
      ? Math.max(0, cur - 1)
      : Math.min(match.quantity, cur + 1);
    const key = String(match.id);
    const nextState = { ...pickedRef.current, [key]: nextQty };
    pickedRef.current = nextState;
    setPicked(nextState);
    setRenderTick(t => t + 1);
    fetch(`/api/warehouse-orders/${order.id}/items/${match.id}/picked`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ quantityPicked: nextQty })
    }).catch(() => {});
    setScanMessage({
      type: 'ok',
      text: isRemove ? `${match.article} ${match.size || ''} −1` : `${match.article} ${match.size || ''} +1`
    });
    setTimeout(() => setScanMessage(null), 2000);
    if (navigator.vibrate) navigator.vibrate(100);
  }, [pickableItems, order.id, picked, scanMode]);

  const savePicked = () => {
    const items = pickableItems;
    const state = pickedRef.current;
    const promises = items.map((it) =>
      fetch(`/api/warehouse-orders/${order.id}/items/${it.id}/picked`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ quantityPicked: state[String(it.id)] ?? picked[String(it.id)] ?? 0 })
      })
    );
    return Promise.all(promises).then(() => onRefresh());
  };

  const dismissWrongScan = () => setWrongScanPrompt(null);

  const addWrongScanToOrder = () => {
    if (!wrongScanPrompt?.barcode) return;
    const barcode = wrongScanPrompt.barcode;
    setWrongScanPrompt(null);
    fetch(`/api/warehouse-orders/${order.id}/items`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ barcode, article: '', color: '', size: '', quantity: 1 })
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(({ id: newItemId }) => {
        return fetch(`/api/warehouse-orders/${order.id}/items/${newItemId}/picked`, {
          method: 'PATCH',
          headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
          body: JSON.stringify({ quantityPicked: 1 })
        }).then(() => fetch(`/api/warehouse-orders/${order.id}`, { headers: getAuthHeaders() }));
      })
      .then(r => r.json())
      .then(data => {
        setDetail(data);
        const nextPicked = {};
        (data.items || []).forEach(it => { nextPicked[String(it.id)] = it.quantity_picked ?? it.quantityPicked ?? 0; });
        setPicked(nextPicked);
        pickedRef.current = { ...nextPicked };
        setScanMessage({ type: 'ok', text: 'Позиция добавлена в заказ' });
        setTimeout(() => setScanMessage(null), 3000);
      })
      .catch(() => setScanMessage({ type: 'error', text: 'Не удалось добавить позицию' }));
  };

  const allPicked = (!pickableItems.length) || pickableItems.every((it) => (pickedRef.current[String(it.id)] ?? picked[String(it.id)] ?? 0) >= it.quantity);

  const removeItemFromOrder = (item, comment) => {
    const line = [item.article, item.color, item.size].filter(Boolean).join(' ');
    const newNote = (detail?.notes || '').trim() + '\nУдалено из сбора: ' + (line || 'позиция') + ' — ' + (comment || 'позиции нет на складе');
    setRemoveItemPrompt(null);
    Promise.all([
      fetch(`/api/warehouse-orders/${order.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: newNote.trim() })
      }),
      fetch(`/api/warehouse-orders/${order.id}/items/${item.id}`, { method: 'DELETE', headers: getAuthHeaders() })
    ])
      .then(([r1]) => r1.ok ? fetch(`/api/warehouse-orders/${order.id}`, { headers: getAuthHeaders() }) : Promise.reject())
      .then(r => r.json())
      .then(data => {
        setDetail(data);
        const nextPicked = {};
        (data.items || []).forEach(it => { nextPicked[String(it.id)] = it.quantity_picked ?? it.quantityPicked ?? 0; });
        setPicked(nextPicked);
        pickedRef.current = { ...nextPicked };
        setScanMessage({ type: 'ok', text: 'Позиция удалена из заказа' });
        setTimeout(() => setScanMessage(null), 2500);
      })
      .catch(() => setScanMessage({ type: 'error', text: 'Не удалось удалить позицию' }));
  };

  const handleCollected = () => {
    savePicked().then(() => onCollected());
  };

  if (!detail && !loading) return null;

  const content = (
    <div
      className="picking-content picking-content-swipe"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
        <div className="picking-header">
          <div className="picking-header-nav">
            {canSwipe && (
              <>
                <button type="button" className="btn-swipe-nav btn-swipe-prev" onClick={handleSwipePrev} disabled={!hasPrev} aria-label="Предыдущий заказ"><FiChevronLeft /></button>
                <span className="picking-header-counter">{currentIndex >= 0 ? currentIndex + 1 : 0} / {list.length}</span>
                <button type="button" className="btn-swipe-nav btn-swipe-next" onClick={handleSwipeNext} disabled={!hasNext} aria-label="Следующий заказ"><FiChevronRight /></button>
              </>
            )}
          </div>
          <h3>Сбор товаров — заказ {order.external_id || `#${order.id}`}</h3>
          <div className="picking-header-actions">
            <button type="button" className="btn-back-to-list" onClick={onClose} title="Назад к списку заказов">Назад к списку</button>
            <button type="button" className="btn-close" onClick={onClose}><FiX /></button>
          </div>
        </div>
        {canSwipe && (
          <p className="picking-swipe-hint">Свайп влево/вправо — другой заказ</p>
        )}
        {!loading && detail && (
          <div className="picking-order-meta-block">
            {pickingSummaryLine && (
              <p className="picking-order-meta-oneline">{pickingSummaryLine}</p>
            )}
            <button
              type="button"
              className="picking-order-meta-toggle"
              onClick={() => setOrderMetaOpen((v) => !v)}
            >
              {orderMetaOpen ? 'Свернуть' : 'Подробнее о заказе'}
            </button>
            {orderMetaOpen && (
              <div className="picking-order-meta-panel">
                {mergedOrder.np_recipient_name && (
                  <div className="picking-order-meta-row">
                    <span className="picking-order-meta-key">Получатель</span>
                    <span className="picking-order-meta-val">{mergedOrder.np_recipient_name}</span>
                  </div>
                )}
                {mergedOrder.np_recipient_phone && (
                  <div className="picking-order-meta-row">
                    <span className="picking-order-meta-key">Телефон</span>
                    <span className="picking-order-meta-val">{mergedOrder.np_recipient_phone}</span>
                  </div>
                )}
                {(mergedOrder.np_city || mergedOrder.np_warehouse_address) && (
                  <div className="picking-order-meta-row">
                    <span className="picking-order-meta-key">Доставка</span>
                    <span className="picking-order-meta-val">
                      {[mergedOrder.np_city, mergedOrder.np_warehouse_address].filter(Boolean).join(' · ')}
                    </span>
                  </div>
                )}
                {mergedOrder.assigned_username && (
                  <div className="picking-order-meta-row">
                    <span className="picking-order-meta-key">Назначенный сборщик</span>
                    <span className="picking-order-meta-val">{mergedOrder.assigned_username}</span>
                  </div>
                )}
                {(mergedOrder.notes || '').trim() && (
                  <div className="picking-order-meta-row picking-order-meta-row--notes">
                    <span className="picking-order-meta-key">Примечания</span>
                    <span className="picking-order-meta-val picking-order-meta-notes">{mergedOrder.notes}</span>
                  </div>
                )}
                {metadataLines.length > 0 && (
                  <div className="picking-order-meta-row picking-order-meta-row--invoice">
                    <span className="picking-order-meta-key">Данные из накладной</span>
                    <ul className="picking-order-meta-invoice-list">
                      {metadataLines.map((line, i) => (
                        <li key={i}>{line}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {scanMessage && (
          <div className={`picking-scan-msg ${scanMessage.type === 'ok' ? 'ok' : 'err'}`}>
            {scanMessage.text}
          </div>
        )}
        {loading ? <p className="picking-loading">Загрузка...</p> : (
          <>
            <div className="picking-items">
              {pickableItems.length === 0 ? (
                <p className="picking-no-pickable-items">
                  {(detail?.items || []).length > 0
                    ? 'Все строки распознаны как служебные данные накладной (не товары). Откройте «Подробнее о заказе» — они перечислены там.'
                    : 'В заказе нет позиций для сбора.'}
                </p>
              ) : (
                <table className="picking-table">
                  <thead>
                    <tr>
                      <th colSpan={2} className="picking-thead-merged">
                        <div className="picking-thead-labels" role="presentation">
                          <span className="picking-thead-label">Товар</span>
                          <span className="picking-thead-label picking-thead-label-qty">Кол-во</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pickableItems.map((it) => (
                      <PickingItemRow
                        key={it.id}
                        item={it}
                        qtyPicked={pickedRef.current[String(it.id)] ?? picked[String(it.id)] ?? 0}
                        maxQty={Math.max(
                          1,
                          Number(
                            it.quantity ??
                              it.qty ??
                              it.count ??
                              it.quantity_required ??
                              it.quantityRequired ??
                              0
                          ) || 0
                        )}
                        onQtyChange={onQtyChange}
                        onSaveItem={savePickedForItem}
                        onRemove={(item) => setRemoveItemPrompt({ item, comment: 'Позиции нет на складе' })}
                      />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="picking-footer">
              <button type="button" className="btn-scan" onClick={() => setShowScanner(true)}>
                <FiCamera /> Сканировать
              </button>
              <button type="button" onClick={savePicked}>Сохранить собранное</button>
              {allPicked && (
                <>
                  <button type="button" className="btn-done" onClick={handleCollected}>Заказ собран</button>
                </>
              )}
            </div>
          </>
        )}
    </div>
  );

  const fullContent = (
    <>
      {content}
      {showScanner && (
        <BarcodeScanner
          onClose={() => setShowScanner(false)}
          onScan={applyScannedBarcode}
          scanMode={scanMode}
          onScanModeChange={setScanMode}
        />
      )}
      {showScanner && wrongScanPrompt && (
        <div className="wrong-scan-prompt-overlay">
          <div className="wrong-scan-prompt-box">
            <p className="wrong-scan-prompt-title">Такой позиции нет в заказе</p>
            <p className="wrong-scan-prompt-barcode">Штрихкод: {wrongScanPrompt.barcode}</p>
            <p className="wrong-scan-prompt-hint">Удалить скан или добавить позицию в заказ?</p>
            <div className="wrong-scan-prompt-actions">
              <button type="button" className="wrong-scan-btn dismiss" onClick={dismissWrongScan}>
                Удалить
              </button>
              <button type="button" className="wrong-scan-btn add" onClick={addWrongScanToOrder}>
                Добавить в заказ
              </button>
            </div>
          </div>
        </div>
      )}
      {removeItemPrompt && (
        <div className="wrong-scan-prompt-overlay remove-item-overlay">
          <div className="wrong-scan-prompt-box remove-item-box">
            <p className="wrong-scan-prompt-title">Удалить позицию из заказа?</p>
            <p className="wrong-scan-prompt-barcode">{removeItemPrompt.item.article} {removeItemPrompt.item.color} {removeItemPrompt.item.size}</p>
            <div className="form-group">
              <label>Причина (например: позиции нет на складе)</label>
              <input
                type="text"
                value={removeItemPrompt.comment}
                onChange={e => setRemoveItemPrompt(prev => prev ? { ...prev, comment: e.target.value } : null)}
                placeholder="Позиции нет на складе"
              />
            </div>
            <div className="wrong-scan-prompt-actions">
              <button type="button" className="wrong-scan-btn dismiss" onClick={() => setRemoveItemPrompt(null)}>
                Отмена
              </button>
              <button type="button" className="wrong-scan-btn add" onClick={() => removeItemFromOrder(removeItemPrompt.item, removeItemPrompt.comment)}>
                Удалить из заказа
              </button>
            </div>
          </div>
        </div>
      )}
      {fullscreenImageUrl && (
        <div className="picking-fullscreen-photo-overlay" onClick={() => setFullscreenImageUrl(null)}>
          <button type="button" className="picking-fullscreen-close" onClick={(e) => { e.stopPropagation(); setFullscreenImageUrl(null); }} aria-label="Закрыть"><FiX /></button>
          <img src={fullscreenImageUrl} alt="Фото товара" className="picking-fullscreen-img" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </>
  );

  if (nested) {
    const nestedContent = (
      <div
        className="picking-overlay picking-overlay-nested"
        role="presentation"
        onClick={e => e.target === e.currentTarget && onClose()}
      >
        <div
          className="wh-picking-shell"
          data-picking-modal="1"
          onClick={e => e.stopPropagation()}
        >
          {fullContent}
        </div>
      </div>
    );
    return ReactDOM.createPortal(nestedContent, document.body);
  }

  // Не используем react-modal, чтобы избежать глобальных oверрайдов по [role="dialog"]
  // (они ломают цвета/фон в "Сбор товаров" и других модалках).
  const portalContent = (
    <div
      className="picking-overlay"
      role="presentation"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="wh-picking-shell"
        data-picking-modal="1"
        role="dialog"
        aria-label="Сбор товаров"
        onClick={(e) => e.stopPropagation()}
      >
        {fullContent}
      </div>
    </div>
  );
  return ReactDOM.createPortal(portalContent, document.body);
}

function BarcodeScanner({ onClose, onScan, scanMode = 'add', onScanModeChange }) {
  const scannerRef = useRef(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let html5Qr = null;
    const start = async () => {
      if (!Html5Qrcode) {
        setErr('Модуль сканирования (html5-qrcode) не установлен. Установите: npm i html5-qrcode');
        return;
      }
      try {
        html5Qr = new Html5Qrcode('barcode-reader');
        await html5Qr.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: (w, h) => Math.min(280, w, h) },
          (decodedText) => { onScan(decodedText); },
          () => {}
        );
        setErr(null);
      } catch (e) {
        setErr(e?.message || 'Не удалось запустить камеру');
      }
    };
    start();
    return () => {
      if (html5Qr && html5Qr.isScanning) {
        html5Qr.stop().catch(() => {});
      }
    };
  }, [onScan]);

  return (
    <div className="barcode-scanner-overlay">
      <div className="barcode-scanner-box">
        <div className="barcode-scanner-header">
          <span>Наведите камеру на штрихкод</span>
          <button type="button" className="btn-close-scanner" onClick={onClose}>Закрыть</button>
        </div>
        <div className="barcode-scanner-mode">
          <button
            type="button"
            className={`btn-scan-mode ${scanMode === 'add' ? 'active' : ''}`}
            onClick={() => onScanModeChange?.('add')}
          >
            Добавить
          </button>
          <button
            type="button"
            className={`btn-scan-mode btn-scan-mode-remove ${scanMode === 'remove' ? 'active' : ''}`}
            onClick={() => onScanModeChange?.('remove')}
          >
            Убрать
          </button>
        </div>
        {err && <div className="barcode-scanner-err">{err}</div>}
        <div id="barcode-reader" className="barcode-reader" />
      </div>
    </div>
  );
}

const EXAMPLE_1C_BODY = {
  externalId: 'ПРИМЕР-001',
  npRecipientName: 'Іванов Іван',
  npRecipientPhone: '+380501234567',
  npCity: 'Київ',
  npWarehouseAddress: 'Відділення №1',
  notes: '',
  items: [
    { article: 'ART-1', color: 'Чорний', size: 'M', quantity: 1, barcode: '2200004108384' }
  ]
};

function WarehouseConnection1CModal({ onClose, onTestSuccess }) {
  const apiUrl = typeof window !== 'undefined' ? `${window.location.origin}/api/warehouse-orders/from-1c` : '';
  const [apiKey, setApiKey] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [copyLabel, setCopyLabel] = useState(null);

  const copyToClipboard = (text, label) => {
    if (!navigator.clipboard) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopyLabel(label);
      setTimeout(() => setCopyLabel(null), 2000);
    });
  };

  const handleTest = () => {
    setTestResult(null);
    const headers = { 'Content-Type': 'application/json' };
    if (apiKey.trim()) headers['X-API-Key'] = apiKey.trim();
    fetch(apiUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(EXAMPLE_1C_BODY)
    })
      .then(r => {
        if (r.status === 201) return r.json().then(() => ({ ok: true }));
        if (r.status === 404) {
          return r.text().then(() => ({
            ok: false,
            status: 404,
            text: 'Эндпоинт не найден. Подключите на сервере роут заказов склада (warehouseOrders).'
          }));
        }
        return r.text().then(t => ({ ok: false, status: r.status, text: t }));
      })
      .then(result => {
        if (result.ok) {
          setTestResult({ type: 'ok', text: 'Подключение успешно. Тестовый заказ создан.' });
          setTimeout(() => onTestSuccess(), 1500);
        } else {
          setTestResult({ type: 'err', text: `Ошибка ${result.status}: ${result.text || ''}` });
        }
      })
      .catch(e => setTestResult({ type: 'err', text: e.message || 'Ошибка сети' }));
  };

  const connectionText = `URL: ${apiUrl}
Метод: POST
Заголовки: Content-Type: application/json${apiKey.trim() ? ', X-API-Key: (тот же ключ, что в .env на сервере)' : ''}

Пример тела запроса — см. в модалке (блок JSON).`;

  const instructionForProgrammer = `========================================
ПОДКЛЮЧЕНИЕ 1С — ОТПРАВКА ЗАКАЗОВ В МЕССЕНДЖЕР
========================================

Скопируйте в 1С и настройте HTTP-запрос (POST на указанный URL).

--- URL (куда отправлять заказы из 1С) ---
${apiUrl}

--- Метод ---
POST

--- Заголовки ---
Content-Type: application/json
${apiKey.trim() ? 'X-API-Key: ' + apiKey.trim() + '\n(если ключ задан в .env на сервере — передавать в заголовке)' : '(API-ключ не задан — заголовок X-API-Key не нужен)'}

--- Тело запроса ---
JSON. Обязательные поля:
• Заказ: номер (externalId / id_1c / number), получатель НП (npRecipientName / recipient_name), телефон (npRecipientPhone), город (npCity), отделение НП (npWarehouseId, npWarehouseAddress), примечание (notes).
• Позиции: массив "items" или "positions". В каждой позиции: article, color, size, quantity; желательно barcode (для сканера).

--- Пример тела (JSON) ---
${JSON.stringify(EXAMPLE_1C_BODY, null, 2)}

--- В 1С ---
При создании/изменении заказа выполнить HTTP POST на URL выше с заголовком Content-Type: application/json и телом в формате JSON (заказ + массив items). Полное описание полей — в файле 1C_ИНТЕГРАЦИЯ_ЗАКАЗЫ.md в проекте мессенджера.
========================================`;

  return (
    <Modal isOpen onRequestClose={onClose} className="warehouse-connection-modal" overlayClassName="picking-overlay" contentLabel="Подключение 1С">
      <div className="warehouse-connection-content">
        <div className="warehouse-connection-header">
          <h3><FiLink /> Подключение 1С</h3>
          <ModalCloseButton onClick={onClose} />
        </div>
        <p className="warehouse-connection-desc">Скопируйте данные ниже и настройте вызов в 1С (HTTP-запрос на этот URL). Либо введите API-ключ и нажмите «Проверить подключение» — тестовый заказ появится в списке заказов.</p>

        <div className="warehouse-connection-block warehouse-connection-block-main">
          <label>Для программиста 1С — скопировать и отправить (всё в одном блоке)</label>
          <button type="button" className="btn-copy-all btn-copy-instruction" onClick={() => copyToClipboard(instructionForProgrammer, 'instruction')}>
            <FiCopy /> Скопировать инструкцию для программиста 1С
          </button>
          {copyLabel === 'instruction' && <span className="warehouse-connection-copied">Скопировано — вставьте в письмо программисту</span>}
        </div>

        <div className="warehouse-connection-block">
          <label>URL для 1С</label>
          <div className="warehouse-connection-row">
            <input type="text" readOnly value={apiUrl} className="warehouse-connection-url" />
            <button type="button" className="btn-copy" onClick={() => copyToClipboard(apiUrl, 'URL')} title="Скопировать">
              <FiCopy /> {copyLabel === 'URL' ? 'Скопировано' : 'Копировать'}
            </button>
          </div>
        </div>

        <div className="warehouse-connection-block">
          <label>API-ключ (если задан в .env на сервере: WAREHOUSE_1C_API_KEY)</label>
          <input
            type="password"
            className="warehouse-connection-input"
            placeholder="Оставьте пустым, если ключ не используется"
            value={apiKey}
            onChange={e => setApiKey(e.target.value)}
          />
        </div>

        <div className="warehouse-connection-block">
          <button type="button" className="btn-copy-all" onClick={() => copyToClipboard(connectionText, 'all')}>
            <FiCopy /> Скопировать все данные для 1С
          </button>
          {copyLabel === 'all' && <span className="warehouse-connection-copied">Скопировано</span>}
        </div>

        <div className="warehouse-connection-block">
          <label>Проверка подключения</label>
          <button type="button" className="btn-test-connection" onClick={handleTest}>
            Отправить тестовый заказ (подключиться)
          </button>
          {testResult && (
            <div className={`warehouse-connection-result ${testResult.type === 'ok' ? 'ok' : 'err'}`}>
              {testResult.text}
            </div>
          )}
        </div>

        <div className="warehouse-connection-block">
          <label>Пример тела запроса (JSON) — вставьте в 1С</label>
          <div className="warehouse-connection-row">
            <pre className="warehouse-connection-json">{JSON.stringify(EXAMPLE_1C_BODY, null, 2)}</pre>
            <button type="button" className="btn-copy" onClick={() => copyToClipboard(JSON.stringify(EXAMPLE_1C_BODY, null, 2), 'json')} title="Скопировать">
              <FiCopy /> {copyLabel === 'json' ? 'Скопировано' : 'Копировать'}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function warehouseUsersFromList(u) {
  return (Array.isArray(u) ? u : []).filter(user => {
    const d = (user.department || '').toLowerCase();
    const p = (user.position || '').toLowerCase();
    return /склад|кладовщик|сборщик|логистик/i.test(d) || /склад|кладовщик|сборщик/i.test(p) || user.role === 'admin';
  });
}

function BulkDistributionModal({ onClose, onAssigned }) {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [evenUserIds, setEvenUserIds] = useState(() => new Set());
  const [assignUserId, setAssignUserId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr('');
    Promise.all([
      fetch('/api/warehouse-orders?status=queue', { headers: getAuthHeaders() }).then(r => {
        if (!r.ok) return Promise.reject(new Error('queue'));
        return r.json();
      }),
      fetch('/api/users', { headers: getAuthHeaders() }).then(r => (r.ok ? r.json() : []))
    ])
      .then(([orders, u]) => {
        if (cancelled) return;
        setRows(Array.isArray(orders) ? orders : []);
        setUsers(warehouseUsersFromList(u));
      })
      .catch(() => {
        if (!cancelled) setErr('Не удалось загрузить очередь заказов');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(o => {
      const id = String(o.id ?? '');
      const ext = String(o.external_id ?? '').toLowerCase();
      const name = (o.np_recipient_name || '').toLowerCase();
      return id.includes(q) || ext.includes(q) || name.includes(q);
    });
  }, [rows, search]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allFilteredSelected = filteredRows.length > 0 && filteredRows.every(o => selected.has(o.id));

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filteredRows.forEach(o => next.delete(o.id));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filteredRows.forEach(o => next.add(o.id));
        return next;
      });
    }
  };

  const toggleEvenUser = (id) => {
    setEvenUserIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllEvenUsers = () => {
    setEvenUserIds(new Set(users.map(u => u.id)));
  };

  const clearEvenUsers = () => {
    setEvenUserIds(new Set());
  };

  const userLabel = (u) => (u.fio || u.username || `#${u.id}`);

  const distributeEvenly = () => {
    setSuccessMsg('');
    if (evenUserIds.size < 1) {
      setErr('Отметьте хотя бы одного сборщика для поровну распределения');
      return;
    }
    const useSubset = selected.size > 0;
    const orderIdsPayload = useSubset ? [...selected] : null;
    const nOrders = useSubset ? orderIdsPayload.length : rows.length;
    if (nOrders < 1) {
      setErr('Нет заказов для распределения');
      return;
    }
    const who = [...evenUserIds].map(uid => users.find(u => u.id === uid)).filter(Boolean);
    const names = who.map(userLabel).join(', ');
    const msg = useSubset
      ? `Распределить ${nOrders} выбранных заказов между ${evenUserIds.size} сборщиками (${names})? Заказы будут перемешаны и разделены поровну.`
      : `Распределить всю очередь (${nOrders} заказов) между ${evenUserIds.size} сборщиками (${names})? Заказы будут перемешаны и разделены поровну (разница у соседей не больше одного заказа).`;
    if (!window.confirm(msg)) return;

    setSubmitting(true);
    setErr('');
    fetch('/api/warehouse-orders/distribute-evenly', {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userIds: [...evenUserIds],
        orderIds: orderIdsPayload,
        shuffle: true
      })
    })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Ошибка распределения');
        return data;
      })
      .then(async (data) => {
        const parts = Object.entries(data.byUser || {})
          .map(([uid, cnt]) => {
            const u = users.find(x => String(x.id) === String(uid));
            return `${userLabel(u || { id: uid })}: ${cnt}`;
          })
          .join('; ');
        setSuccessMsg(`Готово: ${data.total} заказов. ${parts}`);
        setSelected(new Set());
        try {
          const r = await fetch('/api/warehouse-orders?status=queue', { headers: getAuthHeaders() });
          if (r.ok) {
            const orders = await r.json();
            setRows(Array.isArray(orders) ? orders : []);
          }
        } catch (_) { /* ignore */ }
        onAssigned({ keepOpen: true });
      })
      .catch(e => { setErr(e.message || 'Не удалось распределить'); })
      .finally(() => { setSubmitting(false); });
  };

  const submit = () => {
    setSuccessMsg('');
    if (!assignUserId || selected.size === 0) {
      setErr('Выберите заказы и сборщика');
      return;
    }
    setSubmitting(true);
    setErr('');
    fetch('/api/warehouse-orders/bulk-assign', {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: Number(assignUserId), orderIds: [...selected] })
    })
      .then(async r => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.error || 'Ошибка назначения');
        return data;
      })
      .then(() => { onAssigned(); })
      .catch(e => { setErr(e.message || 'Не удалось назначить'); })
      .finally(() => { setSubmitting(false); });
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      className="warehouse-distribute-modal"
      overlayClassName="picking-overlay"
      contentLabel="Распределение заказов"
    >
      <div className="warehouse-distribute-content">
        <div className="warehouse-distribute-header">
          <h3><FiUsers /> Распределить заказы</h3>
          <ModalCloseButton onClick={onClose} />
        </div>
        <p className="warehouse-distribute-intro">
          Отметьте заказы в очереди и выберите сборщика — они перейдут в «Собираются».
        </p>

        {!loading && users.length > 0 && (
          <div className="warehouse-distribute-even-panel">
            <div className="warehouse-distribute-even-title">
              <FiShuffle /> Поровну между сборщиками
            </div>
            <p className="warehouse-distribute-even-desc">
              Отметьте сотрудников. Заказы <strong>перемешиваются</strong> и делятся по кругу: у всех будет почти одинаковое число (разница не больше одного).
              {rows.length > 0 && (
                <> Если в списке ниже <strong>ничего не выделено</strong> — распределяется <strong>вся очередь</strong> ({rows.length} шт.).</>
              )}
            </p>
            <div className="warehouse-distribute-even-actions">
              <button type="button" className="warehouse-distribute-even-link" onClick={selectAllEvenUsers}>Все сборщики</button>
              <button type="button" className="warehouse-distribute-even-link" onClick={clearEvenUsers}>Снять всех</button>
            </div>
            <ul className="warehouse-distribute-even-users">
              {users.map(u => (
                <li key={u.id}>
                  <label className="warehouse-distribute-even-user-label">
                    <input
                      type="checkbox"
                      checked={evenUserIds.has(u.id)}
                      onChange={() => toggleEvenUser(u.id)}
                    />
                    <span>{userLabel(u)}</span>
                  </label>
                </li>
              ))}
            </ul>
            <button
              type="button"
              className="warehouse-distribute-even-submit"
              disabled={submitting || evenUserIds.size < 1 || rows.length < 1}
              onClick={distributeEvenly}
            >
              {submitting ? 'Распределение…' : 'Раздать поровну'}
            </button>
          </div>
        )}

        {successMsg && <div className="warehouse-distribute-success">{successMsg}</div>}
        {err && <div className="warehouse-distribute-error">{err}</div>}
        {loading && <div className="warehouse-distribute-loading">Загрузка...</div>}
        {!loading && (
          <>
            <div className="warehouse-distribute-toolbar">
              <div className="warehouse-distribute-search">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Поиск по №, внешнему №, имени"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <label className="warehouse-distribute-select-all">
                <input type="checkbox" checked={allFilteredSelected} onChange={toggleSelectAllFiltered} />
                <span>Выбрать все на экране ({filteredRows.length})</span>
              </label>
            </div>
            <div className="warehouse-distribute-list-wrap">
              {filteredRows.length === 0 ? (
                <div className="warehouse-distribute-empty">Нет заказов в очереди</div>
              ) : (
                <ul className="warehouse-distribute-list">
                  {filteredRows.map(o => (
                    <li
                      key={o.id}
                      className={`warehouse-distribute-row ${selected.has(o.id) ? 'selected' : ''}`}
                      onClick={() => toggle(o.id)}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggle(o.id);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="warehouse-distribute-checkbox-col">
                        <input
                          type="checkbox"
                          checked={selected.has(o.id)}
                          onChange={() => toggle(o.id)}
                          onClick={e => e.stopPropagation()}
                        />
                        <span className="warehouse-distribute-items-count" title="Число позиций в заказе">
                          {Number(o.items_count ?? o.itemsCount ?? 0)} поз.
                        </span>
                      </div>
                      <div className="warehouse-distribute-row-main">
                        <span className="warehouse-distribute-id">{o.external_id || `#${o.id}`}</span>
                        {o.np_recipient_name && <span className="warehouse-distribute-name">{o.np_recipient_name}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="warehouse-distribute-footer">
              <select
                value={assignUserId}
                onChange={e => setAssignUserId(e.target.value)}
                className="warehouse-distribute-assignee"
              >
                <option value="">Сборщик</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.fio || u.username}</option>
                ))}
              </select>
              <button type="button" className="warehouse-distribute-submit" disabled={submitting} onClick={submit}>
                {submitting ? 'Отправка…' : 'Назначить выбранные'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Тон бейджа процента собранных из выданных */
function warehouseStatsPctTone(pct) {
  const n = Number(pct);
  if (Number.isNaN(n)) return 'empty';
  if (n >= 100) return 'complete';
  if (n >= 70) return 'high';
  if (n >= 40) return 'mid';
  return 'low';
}

const WAREHOUSE_STATS_DONUT_COLOR = {
  complete: '#059669',
  high: '#0d9488',
  mid: '#d97706',
  low: '#ea580c',
  empty: '#94a3b8'
};

function WarehouseOrdersStatsModal({ onClose }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tick, setTick] = useState(0);

  const loadStats = useCallback(() => {
    setLoading(true);
    setError('');
    fetch('/api/warehouse-orders/stats/by-assignee', { headers: getAuthHeaders() })
      .then(r => {
        if (!r.ok) throw new Error('Доступ запрещён');
        return r.json();
      })
      .then(data => setList(Array.isArray(data) ? data : []))
      .catch(e => setError(e.message || 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats, tick]);

  const refresh = () => setTick(t => t + 1);

  return (
    <Modal isOpen onRequestClose={onClose} className="warehouse-stats-modal" overlayClassName="picking-overlay picking-overlay--warehouse-stats" contentLabel="Заказы по сборщикам">
      <div className="warehouse-stats-content">
        <div className="warehouse-stats-header">
          <h3><FiBarChart2 /> Мониторинг сборки</h3>
          <div className="warehouse-stats-header-actions">
            <button type="button" className="warehouse-stats-refresh" onClick={refresh} disabled={loading} title="Обновить">
              <FiRefreshCw className={loading ? 'warehouse-stats-refresh-spin' : ''} />
              <span className="warehouse-stats-refresh-label">Обновить</span>
            </button>
            <ModalCloseButton inline onClick={onClose} />
          </div>
        </div>
        {error && <div className="warehouse-stats-error">{error}</div>}
        {loading && <div className="warehouse-stats-loading">Загрузка...</div>}
        {!loading && !error && (
          <div className="warehouse-stats-table-wrap">
            <table className="warehouse-stats-table">
              <thead>
                <tr>
                  <th colSpan={7} className="warehouse-stats-thead-wrap">
                    <div className="warehouse-stats-thead-grid" role="row">
                      <span title="Сотрудник">Сотрудник</span>
                      <span title="Выдано">Выдано</span>
                      <span title="Собираются">
                        <span className="warehouse-stats-th-full">Собираются</span>
                        <span className="warehouse-stats-th-compact">Собир.</span>
                      </span>
                      <span title="Собрано">Собрано</span>
                      <span title="На отправке">
                        <span className="warehouse-stats-th-full">На отправке</span>
                        <span className="warehouse-stats-th-compact">Отпр.</span>
                      </span>
                      <span title="В очереди у сотрудника">
                        <span className="warehouse-stats-th-full">В очереди у сотр.</span>
                        <span className="warehouse-stats-th-compact">Очередь</span>
                      </span>
                      <span title="Собрано из выданных">
                        <span className="warehouse-stats-th-full">Собрано из выданных</span>
                        <span className="warehouse-stats-th-compact">% из выд.</span>
                      </span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr className="warehouse-stats-card-row">
                    <td colSpan={7} className="warehouse-stats-card-cell">
                      <div className="warehouse-stats-picker-card warehouse-stats-picker-card--empty">
                        <p className="warehouse-stats-empty">Нет назначенных заказов</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  list.map(row => {
                    const dist = row.distributed_total != null ? row.distributed_total : (
                      (row.in_progress || 0) + (row.collected || 0) + (row.shipping || 0)
                    );
                    const collected = row.collected || 0;
                    const inProgress = row.in_progress || 0;
                    const shipping = row.shipping || 0;
                    const queue = row.queue || 0;
                    const pct = row.percent_assembled_of_distributed != null
                      ? row.percent_assembled_of_distributed
                      : (dist > 0 ? Math.round((collected / dist) * 100) : null);
                    const pctNum = pct ?? 0;
                    const tone = warehouseStatsPctTone(pctNum);
                    const donutColor = WAREHOUSE_STATS_DONUT_COLOR[tone] || WAREHOUSE_STATS_DONUT_COLOR.mid;
                    return (
                      <tr key={row.userId} className="warehouse-stats-card-row">
                        <td colSpan={7} className="warehouse-stats-card-cell">
                          <div className="warehouse-stats-picker-card">
                            <div className="warehouse-stats-user">
                              <span className="warehouse-stats-avatar-wrap">
                                {row.avatar ? (
                                  <img src={row.avatar} alt="" className="warehouse-stats-avatar" />
                                ) : (
                                  <span className="warehouse-stats-avatar-placeholder"><FiUser /></span>
                                )}
                              </span>
                              <div className="warehouse-stats-user-info">
                                <span className="warehouse-stats-fio">{row.fio || row.username || '—'}</span>
                                {dist > 0 ? (
                                  <span
                                    className={`warehouse-stats-pct-badge warehouse-stats-pct-badge--${tone}`}
                                    title={`Собрано ${collected} из ${dist} выданных заказов (${pctNum}%)`}
                                  >
                                    <span className="warehouse-stats-pct-badge-value">{pctNum}%</span>
                                    <span className="warehouse-stats-pct-badge-caption">собрано</span>
                                  </span>
                                ) : (
                                  <span className="warehouse-stats-pct-badge warehouse-stats-pct-badge--empty" title="Нет заказов в работе у сборщика">
                                    нет выданных
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="warehouse-stats-metrics-row">
                              <div className="warehouse-stats-card-metric">
                                <span className="warehouse-stats-metric-label">Выдано</span>
                                <span className="warehouse-stats-num warehouse-stats-num--total" title="Всего выдано на сборку">{dist}</span>
                              </div>
                              <div className="warehouse-stats-card-metric">
                                <span className="warehouse-stats-metric-label">Собираются</span>
                                <span className="warehouse-stats-num warehouse-stats-num--progress" title="Сейчас собираются">{inProgress}</span>
                              </div>
                              <div className="warehouse-stats-card-metric">
                                <span className="warehouse-stats-metric-label">Собрано</span>
                                <span className="warehouse-stats-num warehouse-stats-num--collected" title="Уже в статусе «Собрано»">{collected}</span>
                              </div>
                              <div className="warehouse-stats-card-metric">
                                <span className="warehouse-stats-metric-label">На отправке</span>
                                <span className="warehouse-stats-num warehouse-stats-num--shipping" title="На отправке">{shipping}</span>
                              </div>
                              <div className="warehouse-stats-card-metric">
                                <span className="warehouse-stats-metric-label">В очереди</span>
                                <span className="warehouse-stats-num warehouse-stats-num--queue" title="В очереди у этого сотрудника">{queue}</span>
                              </div>
                            </div>
                            <div className="warehouse-stats-percent">
                              {dist > 0 ? (
                                <div className="warehouse-stats-percent-stack">
                                  <span className="warehouse-stats-percent-section-title">Собрано из выданных</span>
                                  <div className="warehouse-stats-percent-top">
                                    <span className="warehouse-stats-fraction">{collected} / {dist}</span>
                                    <span
                                      className={`warehouse-stats-pct-donut warehouse-stats-pct-donut--${tone}`}
                                      title={`${pctNum}% собрано из выданных`}
                                      style={{
                                        background: `conic-gradient(from -90deg, ${donutColor} 0 ${pctNum}%, rgba(13, 115, 119, 0.14) ${pctNum}% 100%)`
                                      }}
                                    >
                                      <span className="warehouse-stats-pct-donut-inner">
                                        <b>{pctNum}</b>
                                        <small>%</small>
                                      </span>
                                    </span>
                                  </div>
                                  <div className="warehouse-stats-progress-bar warehouse-stats-progress-bar--assembly">
                                    <div
                                      className="warehouse-stats-progress-fill warehouse-stats-progress-fill--assembly"
                                      style={{ width: `${pctNum}%` }}
                                    />
                                  </div>
                                  <span className={`warehouse-stats-pct-foot warehouse-stats-pct-foot--${tone}`}>доля собранных от выданных</span>
                                </div>
                              ) : (
                                <span className="warehouse-stats-na">—</span>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function ImportOrdersExcelModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const upload = () => {
    setError('');
    setResult(null);
    if (!file) {
      setError('Выберите Excel файл (.xlsx или .xls)');
      return;
    }
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    fetch('/api/warehouse-orders/import/excel', {
      method: 'POST',
      headers: getAuthHeaders(),
      body: form
    })
      .then(async (r) => {
        if (r.ok) return r.json();
        const t = await r.text().catch(() => '');
        throw new Error(t || `Ошибка импорта: ${r.status}`);
      })
      .then((data) => {
        setResult(data || {});
        setTimeout(() => onImported?.(data), 400);
      })
      .catch((e) => setError(e.message || 'Ошибка импорта Excel'))
      .finally(() => setUploading(false));
  };

  return (
    <Modal
      isOpen
      onRequestClose={onClose}
      className="add-order-modal"
      overlayClassName="picking-overlay"
      contentLabel="Импорт заказов из Excel"
    >
      <div className="add-order-content">
        <h3><FiUpload /> Импорт заказов из Excel</h3>
        <ModalCloseButton onClick={onClose} />

        <div className="form-group">
          <label>Файл Excel (.xlsx/.xls)</label>
          <input
            type="file"
            accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            onChange={(e) => {
              const f = e.target.files?.[0] || null;
              setFile(f);
              setFileName(f?.name || '');
              setError('');
              setResult(null);
              e.target.value = '';
            }}
          />
          {fileName && (
            <div style={{ marginTop: 6, opacity: 0.85 }}>
              Выбрано: <b>{fileName}</b>
            </div>
          )}
        </div>

        {error && <div className="warehouse-orders-error" style={{ marginTop: 8 }}>{error}</div>}
        {result && !error && (
          <div style={{ marginTop: 10, padding: 10, background: 'rgba(16,185,129,0.12)', borderRadius: 10 }}>
            Импорт выполнен. Создано: <b>{result.createdCount ?? 0}</b> из <b>{result.parsedCount ?? 0}</b>
          </div>
        )}

        <div className="modal-actions">
          <button type="button" onClick={onClose} disabled={uploading}>Отмена</button>
          <button type="button" onClick={upload} disabled={uploading}>
            {uploading ? 'Импорт...' : 'Загрузить и создать'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AddOrderModal({ onClose, onSaved }) {
  const [externalId, setExternalId] = useState('');
  const [npName, setNpName] = useState('');
  const [npPhone, setNpPhone] = useState('');
  const [npCity, setNpCity] = useState('');
  const [npWarehouse, setNpWarehouse] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState([{ article: '', color: '', size: '', quantity: 1, barcode: '', image_url: '' }]);
  const [saving, setSaving] = useState(false);

  const addRow = () => setItems(prev => [...prev, { article: '', color: '', size: '', quantity: 1, barcode: '', image_url: '' }]);
  const removeRow = (i) => setItems(prev => prev.filter((_, j) => j !== i));
  const setItem = (i, field, value) => {
    setItems(prev => {
      const next = [...prev];
      next[i] = { ...next[i], [field]: field === 'quantity' ? parseInt(value, 10) || 0 : value };
      return next;
    });
  };

  const save = () => {
    const body = {
      externalId: externalId || null,
      npRecipientName: npName || null,
      npRecipientPhone: npPhone || null,
      npCity: npCity || null,
      npWarehouseAddress: npWarehouse || null,
      notes: notes || null,
      items: items.filter(it => it.article.trim()).map(it => ({
        article: it.article.trim(),
        color: (it.color || '').trim(),
        size: (it.size || '').trim(),
        quantity: it.quantity || 1,
        barcode: (it.barcode || '').trim() || null,
        image_url: (it.image_url || '').trim() || null
      }))
    };
    if (!body.items.length) return;
    setSaving(true);
    fetch('/api/warehouse-orders', {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => onSaved())
      .finally(() => setSaving(false));
  };

  return (
    <Modal isOpen onRequestClose={onClose} className="add-order-modal" overlayClassName="picking-overlay" contentLabel="Добавить заказ">
      <div className="add-order-content">
        <h3>Добавить заказ</h3>
        <ModalCloseButton onClick={onClose} />
        <div className="form-group">
          <label>Номер в 1С</label>
          <input value={externalId} onChange={e => setExternalId(e.target.value)} placeholder="Необязательно" />
        </div>
        <div className="form-group">
          <label>Получатель НП</label>
          <input value={npName} onChange={e => setNpName(e.target.value)} placeholder="ФИО" />
        </div>
        <div className="form-group">
          <label>Телефон</label>
          <input value={npPhone} onChange={e => setNpPhone(e.target.value)} placeholder="+380..." />
        </div>
        <div className="form-group">
          <label>Город</label>
          <input value={npCity} onChange={e => setNpCity(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Отделение НП</label>
          <input value={npWarehouse} onChange={e => setNpWarehouse(e.target.value)} placeholder="Адрес или №" />
        </div>
        <div className="form-group">
          <label>Примечание</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Позиции</label>
          {items.map((it, i) => (
            <div key={i} className="item-row">
              <input placeholder="Артикул" value={it.article} onChange={e => setItem(i, 'article', e.target.value)} />
              <input placeholder="Цвет" value={it.color} onChange={e => setItem(i, 'color', e.target.value)} />
              <input placeholder="Размер" value={it.size} onChange={e => setItem(i, 'size', e.target.value)} />
              <input type="number" min={1} value={it.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />
              <input placeholder="Штрихкод" value={it.barcode || ''} onChange={e => setItem(i, 'barcode', e.target.value)} className="item-barcode-input" />
              <input placeholder="Фото (URL)" value={it.image_url || ''} onChange={e => setItem(i, 'image_url', e.target.value)} className="item-image-url-input" title="Ссылка на фото товара" />
              <button type="button" onClick={() => removeRow(i)}><FiX /></button>
            </div>
          ))}
          <button type="button" onClick={addRow}><FiPlus /> Добавить позицию</button>
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Отмена</button>
          <button type="button" onClick={save} disabled={saving}>{saving ? 'Сохранение...' : 'Создать'}</button>
        </div>
      </div>
    </Modal>
  );
}

function EditOrderModal({ order, onClose, onSaved }) {
  const [npName, setNpName] = useState(order.np_recipient_name || '');
  const [npPhone, setNpPhone] = useState(order.np_recipient_phone || '');
  const [npCity, setNpCity] = useState(order.np_city || '');
  const [npWarehouse, setNpWarehouse] = useState(order.np_warehouse_address || '');
  const [notes, setNotes] = useState(order.notes || '');
  const [saving, setSaving] = useState(false);

  const save = () => {
    setSaving(true);
    fetch(`/api/warehouse-orders/${order.id}`, {
      method: 'PATCH',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        npRecipientName: npName,
        npRecipientPhone: npPhone,
        npCity: npCity,
        npWarehouseAddress: npWarehouse,
        notes
      })
    })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(() => onSaved())
      .finally(() => setSaving(false));
  };

  return (
    <Modal isOpen onRequestClose={onClose} className="add-order-modal" overlayClassName="picking-overlay" contentLabel="Изменить заказ">
      <div className="add-order-content">
        <h3>Изменить заказ {order.external_id || `#${order.id}`}</h3>
        <ModalCloseButton onClick={onClose} />
        <div className="form-group">
          <label>Получатель НП</label>
          <input value={npName} onChange={e => setNpName(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Телефон</label>
          <input value={npPhone} onChange={e => setNpPhone(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Город</label>
          <input value={npCity} onChange={e => setNpCity(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Отделение НП</label>
          <input value={npWarehouse} onChange={e => setNpWarehouse(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Примечание</label>
          <input value={notes} onChange={e => setNotes(e.target.value)} />
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Отмена</button>
          <button type="button" onClick={save} disabled={saving}>{saving ? 'Сохранение...' : 'Сохранить'}</button>
        </div>
      </div>
    </Modal>
  );
}
